'use client';

import { useState, useEffect, useCallback } from 'react';
import { styled } from '@classify/react-core-components/styles';
import { List } from '@mui/material';
import Skeleton from '@classify/react-core-components/Skeleton';
import { LearningOutcomeListItem } from '@openclassrooms/ui-web/learningOutcomes';
import { RichContent } from '@openclassrooms/rich-content';
import Typography from '@classify/react-core-components/Typography';
import Divider from '@classify/react-core-components/Divider';
import { translate } from '@openclassrooms/translations';
import { useUserQuiz, useResetQuizSession } from '@openclassrooms/data-connect-web/users';
import { useCreateQuizSession, useExerciseSession } from '@openclassrooms/data-connect-web/assessment';
import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import type { ExerciseSession } from '@openclassrooms/sdk-web';
import CourseTitle from '../../../[chapterSlug]/_components/CourseTitle';
import BannerWrapper from '../../../[chapterSlug]/_components/BannerWrapper';
import BottomBanner from '../../../[chapterSlug]/_components/BottomBanner/BottomBanner';
import { useExerciseContext } from '../_contexts/ExerciseContext';
import QuizCompletionBanner from './QuizCompletionBanner';
import QuizQuestions from './QuizQuestions';
const QUIZ_COMPLETED_STATUSES = ['retry', 'failed', 'passed'] as const;
const Content = styled(RichContent)(({
  theme: {
    spacing
  }
}) => ({
  margin: spacing(3, 0, 8),
  '& > p': {
    margin: `${0} !important`
  }
}));
const QuizInformationWrapper = styled('div')(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  padding: spacing(2, 2, 0, 2),
  [breakpoints.up('sm')]: {
    padding: spacing(3, 3, 0, 3)
  }
}));
const ExerciseWrapper = styled('div')(({
  theme: {
    spacing,
    palette
  }
}) => ({
  border: `1px solid ${(palette as any).stroke || palette.divider}`,
  borderRadius: spacing(0.5),
  marginBottom: spacing(4)
}));
export default function ExerciseContentWrapper() {
  const {
    exerciseId,
    initialExerciseSession,
    initialQuizInfo
  } = useExerciseContext();
  const {
    userId
  } = useCurrentUserContext();
  const hasServerData = Boolean(initialExerciseSession);
  const [exerciseSession, setExerciseSession] = useState<ExerciseSession | null>(initialExerciseSession as ExerciseSession ?? null);
  const [isReadyToFetchExerciseSession, setIsReadyToFetchExerciseSession] = useState(false);
  const [hasResetQuiz, setHasResetQuiz] = useState(initialQuizInfo?.learningActivityStatus === 'retry');
  const [createQuizSession] = useCreateQuizSession({
    userId,
    quizId: String(exerciseId)
  });
  const [, {
    mutateAsync: resetQuizSession
  }] = useResetQuizSession();
  const {
    data: userQuizSessionData,
    refetch: refetchQuizInformation
  } = useUserQuiz({
    userId,
    quizId: String(exerciseId)
  }, {
    enabled: Boolean(userId) && !hasServerData,
    refetchOnWindowFocus: false
  });

  // TODO: See if we truly need to create a quiz session here, can it be done server-side fully?
  const onGetUserQuizSessionDataSuccess = useCallback(async (quizSessionData: any) => {
    if (!quizSessionData?.quizSessionId) {
      createQuizSession({
        userId,
        quizId: String(exerciseId)
      }, {
        onSuccess: () => {
          refetchQuizInformation();
        }
      });
    }
  }, [createQuizSession, exerciseId, userId, refetchQuizInformation]);
  useEffect(() => {
    if (hasServerData) return;
    if (userQuizSessionData) {
      onGetUserQuizSessionDataSuccess(userQuizSessionData);
    }
  }, [userQuizSessionData, onGetUserQuizSessionDataSuccess, hasServerData]);
  useEffect(() => {
    if (!exerciseSession) {
      return;
    }
    if (exerciseId !== exerciseSession.id) {
      setExerciseSession(null);
    }
  }, [exerciseId, exerciseSession]);
  useEffect(() => {
    if (hasServerData) return;
    (async () => {
      if (userQuizSessionData && userQuizSessionData.learningActivityStatus === 'retry' && hasResetQuiz === false) {
        await resetQuizSession({
          userId,
          quizId: String(exerciseId)
        });
        setHasResetQuiz(true);
        refetchQuizInformation();
      } else if (userQuizSessionData) {
        setIsReadyToFetchExerciseSession(true);
      }
    })();
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [userQuizSessionData, hasServerData]);
  const {
    quizSessionId
  } = userQuizSessionData || {};
  const {
    data: exerciseSessionData
  } = useExerciseSession({
    exerciseSessionId: quizSessionId
  }, {
    enabled: Boolean(quizSessionId) && isReadyToFetchExerciseSession,
    refetchOnWindowFocus: false
  });
  useEffect(() => {
    if (!hasServerData && exerciseSessionData) {
      setExerciseSession(exerciseSessionData as unknown as ExerciseSession);
    }
  }, [exerciseSessionData, hasServerData]);
  if (!exerciseSession) {
    return <>
        <Skeleton variant="rectangular" height={350} sx={{
        mt: 2
      }} />
        <Skeleton variant="text" sx={{
        my: 2
      }} width={175} />
        <Skeleton variant="rectangular" height={150} />
      </>;
  }
  const {
    title,
    description,
    exerciseLearningOutcomes,
    questions
  } = exerciseSession;
  const quizData = hasServerData ? initialQuizInfo : userQuizSessionData;
  const {
    learningActivityStatus: quizLearningStatus,
    reinitializationAvailableAt
  } = quizData as any || {};
  return <>
      {(quizLearningStatus && QUIZ_COMPLETED_STATUSES.includes(quizLearningStatus as (typeof QUIZ_COMPLETED_STATUSES)[number]) || hasResetQuiz) && <QuizCompletionBanner quizLearningStatus={quizLearningStatus as any} quizReinitializationDate={reinitializationAvailableAt} hasResetQuiz={hasResetQuiz} />}

      <CourseTitle>{title}</CourseTitle>

      <ExerciseWrapper>
        {exerciseLearningOutcomes && <QuizInformationWrapper>
            <Typography variant="h3" component="h2">
              {translate('mcq.view.message.skill_title')}
            </Typography>
            <Divider />

            <List sx={{
          my: 2
        }}>
              {exerciseLearningOutcomes.map(({
            name,
            learningOutcomeId
          }) => <LearningOutcomeListItem title={name} key={learningOutcomeId} hasSmallIcon />)}
            </List>
          </QuizInformationWrapper>}

        {description && <QuizInformationWrapper>
            <Typography variant="h3" component="h2">
              {translate('mcq.view.message.description_title')}
            </Typography>
            <Divider />
            <Content content={description} />
          </QuizInformationWrapper>}

        {questions && <QuizQuestions exerciseId={exerciseId} exerciseSession={exerciseSession} setExerciseSession={setExerciseSession} refetchQuizInformation={refetchQuizInformation} setHasResetQuiz={setHasResetQuiz} />}
      </ExerciseWrapper>

      <BannerWrapper>
        <BottomBanner ctaDataEvent="Navigation|Redirection::Internal|Quiz Apply" />
      </BannerWrapper>
    </>;
}